import { Component, computed, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { MarketplaceSearchService } from '@core/services/marketplace-search.service';
import { AuthService } from '@core/services/auth.service';
import { CartService } from '@core/services/cart.service';
import { ConfirmDialogComponent } from '@shared/components/confirm-dialog/confirm-dialog.component';
import { ToastContainerComponent } from '@shared/components/toast/toast.component';
import { ReloadIfCurrentDirective } from '@shared/directives/reload-if-current.directive';
import { environment } from '@environments/environment';

/**
 * Cascarón FAISEN (fuera de cualquier tienda): directorio de tiendas y la cuenta global
 * del cliente (login, registro, verificación, recuperación y "Mi cuenta").
 */
@Component({
  selector: 'app-marketplace-layout',
  imports: [RouterOutlet, RouterLink, ReloadIfCurrentDirective, ToastContainerComponent, ConfirmDialogComponent],
  host: { '(document:click)': 'closeMenuOnOutsideClick($event)' },
  template: `
    <div class="min-h-screen bg-slate-50 flex flex-col font-sans">
      <header class="bg-primary-50 border-b border-primary-100 sticky top-0 z-30">
        <!-- Constelación de marca de fondo: detrás de todo el contenido del encabezado. -->
        <div class="absolute inset-0 z-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <svg class="absolute inset-0 size-full" viewBox="0 0 1000 100" preserveAspectRatio="none" fill="none">
            <path d="M 0,72 C 160,8 300,104 470,46 S 760,4 1000,52" stroke="var(--color-primary-300)" stroke-width="1.5" stroke-dasharray="6 8" stroke-opacity="0.8" vector-effect="non-scaling-stroke" class="animate-dash-flow" />
            <path d="M 0,18 C 220,92 420,-2 640,60 S 900,92 1000,30" stroke="var(--color-secondary-400)" stroke-width="1.5" stroke-opacity="0.55" vector-effect="non-scaling-stroke" />
            <path d="M 300,100 Q 430,38 560,72" stroke="var(--color-primary-500)" stroke-width="1.5" stroke-opacity="0.35" vector-effect="non-scaling-stroke" />
          </svg>
          <span class="brand-node bg-primary-400 text-primary-400 animate-node-glow" style="top:32%;left:9%"></span>
          <span class="brand-node bg-secondary-300 text-secondary-300 animate-node-glow" style="top:74%;left:23%;animation-delay:0.8s"></span>
          <span class="brand-node brand-node-lg bg-secondary-400 text-secondary-400 animate-node-glow" style="top:40%;left:39%;animation-delay:1.5s"></span>
          <span class="brand-node bg-primary-300 text-primary-300 animate-node-glow" style="top:70%;left:56%;animation-delay:0.4s"></span>
          <span class="brand-node bg-primary-500 text-primary-500 animate-node-glow" style="top:30%;left:72%;animation-delay:2.2s"></span>
          <span class="brand-node bg-secondary-400 text-secondary-400 animate-node-glow" style="top:60%;left:90%;animation-delay:1.1s"></span>
        </div>
        <div class="relative z-10 max-w-7xl mx-auto px-4 h-16 flex items-center justify-between gap-3">
          <a routerLink="/" reloadIfCurrent class="flex items-center gap-3">
            <img src="faisen-icon.png" alt="" class="w-9 h-9 object-contain" />
            <div>
              <span class="font-bold text-slate-900 text-lg tracking-tight">FAISEN</span>
              <span class="hidden sm:inline text-xs bg-primary-50 text-primary-700 font-semibold px-2 py-0.5 rounded-full ml-2 border border-primary-100">Marketplace</span>
            </div>
          </a>

          <div class="relative flex-1 max-w-xl mx-2">
              <input
                type="search"
                maxlength="100"
                [value]="search.query()"
                (input)="onSearch($any($event.target).value)"
                placeholder="Buscar productos o tiendas..."
                aria-label="Buscar productos o tiendas"
                class="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 bg-white/90 text-slate-900 text-sm placeholder:text-slate-400 focus:bg-white focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-500/20"
              />
              <svg class="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
              </svg>
            </div>

          <div class="flex items-center gap-2">
            @if (cart.storeCarts().length) {
              <div class="relative" data-cart-menu>
                <button
                  type="button"
                  (click)="cartMenuOpen.set(!cartMenuOpen())"
                  (keydown.escape)="cartMenuOpen.set(false)"
                  [attr.aria-expanded]="cartMenuOpen()"
                  title="Mis carritos"
                  aria-label="Mis carritos"
                  class="relative flex items-center text-slate-700 hover:text-primary-700 bg-white/80 hover:bg-white px-2.5 py-2 rounded-xl transition-all"
                >
                  <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" />
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                  </svg>
                  <span class="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-secondary-500 text-white text-[10px] font-bold leading-4 text-center">{{ cartCount() }}</span>
                </button>
                @if (cartMenuOpen()) {
                  <div class="absolute right-0 top-full mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-100 py-2 text-sm z-50">
                    <div class="px-4 py-2 text-xs text-slate-400 border-b border-slate-100">Tus carritos</div>
                    @for (c of cart.storeCarts(); track c.slug) {
                      <a [routerLink]="['/', c.slug, 'carrito']" (click)="cartMenuOpen.set(false)" class="flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50">
                        @if (c.logoUrl) {
                          <img [src]="c.logoUrl" alt="" class="w-8 h-8 rounded-lg object-contain border border-slate-100 shrink-0" />
                        } @else {
                          <span class="w-8 h-8 rounded-lg bg-primary-50 text-primary-700 flex items-center justify-center font-bold uppercase shrink-0">{{ c.storeName.charAt(0) }}</span>
                        }
                        <span class="flex-1 min-w-0">
                          <span class="block font-semibold text-slate-900 truncate">{{ c.storeName }}</span>
                          <span class="block text-xs text-slate-500">{{ c.totalItems }} {{ c.totalItems === 1 ? 'producto' : 'productos' }}</span>
                        </span>
                        <span class="text-xs font-semibold text-primary-700 whitespace-nowrap">Ver →</span>
                      </a>
                    }
                  </div>
                }
              </div>
            }
            @if (auth.user()) {
              <a routerLink="/cuenta" [queryParams]="{ tab: 'orders' }" class="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-primary-700 bg-white/80 hover:bg-white px-2.5 py-2 rounded-xl transition-all" title="Mis pedidos" aria-label="Mis pedidos">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M21 8 12 3 3 8v8l9 5 9-5z" /><path d="m3 8 9 5 9-5M12 13v8" /></svg>
              </a>
              <a routerLink="/cuenta" [queryParams]="{ tab: 'addresses' }" class="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-primary-700 bg-white/80 hover:bg-white px-2.5 py-2 rounded-xl transition-all" title="Mis direcciones" aria-label="Mis direcciones">
                <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" /><circle cx="12" cy="10" r="3" /></svg>
              </a>
            }
            @if (auth.user(); as user) {
              <div class="relative" data-user-menu>
                <button
                  type="button"
                  (click)="menuOpen.set(!menuOpen())"
                  (keydown.escape)="menuOpen.set(false)"
                  [attr.aria-expanded]="menuOpen()"
                  class="flex items-center gap-2 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-xl transition-all"
                >
                  <span class="w-6 h-6 rounded-full bg-primary-600 text-white flex items-center justify-center uppercase overflow-hidden">
                    @if (user.avatarUrl) {
                      <img [src]="user.avatarUrl" alt="" referrerpolicy="no-referrer" class="w-full h-full object-cover" />
                    } @else {
                      {{ user.firstName.charAt(0) }}
                    }
                  </span>
                  <span class="hidden sm:inline">{{ user.firstName }}</span>
                </button>
                @if (menuOpen()) {
                  <div class="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-slate-100 py-2 text-sm z-50">
                    <div class="px-4 py-2 text-xs text-slate-400 border-b border-slate-100 truncate">{{ user.email }}</div>
                    <a routerLink="/cuenta" (click)="menuOpen.set(false)" class="block px-4 py-2 text-slate-700 hover:bg-slate-50">Mi cuenta</a>
                    <button type="button" (click)="logout()" class="w-full text-left px-4 py-2 text-red-600 hover:bg-slate-50 font-medium">Cerrar sesión</button>
                  </div>
                }
              </div>
            } @else {
              <a routerLink="/auth/login" class="text-xs font-semibold text-slate-700 hover:text-primary-600 px-3 py-2">Iniciar sesión</a>
              <a routerLink="/auth/register" class="text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 px-3 py-2 rounded-xl transition-all">Crear cuenta</a>
            }
            <a
              [href]="adminUrl"
              target="_blank"
              class="hidden md:flex text-xs font-semibold text-slate-600 hover:text-primary-600 items-center gap-1 bg-slate-100 hover:bg-slate-200 px-3 py-2 rounded-xl transition-all"
            >
              Acceso Negocios
            </a>
          </div>
        </div>
      </header>

      <div class="flex-1">
        <router-outlet />
      </div>

      <footer class="bg-white text-slate-500 py-5 border-t border-slate-200 text-xs text-center">
        <div class="max-w-7xl mx-auto px-4 space-y-2">
          <p>© {{ year }} FAISEN Platform. Directorio oficial de comercio electrónico multi-tenant.</p>
        </div>
      </footer>
      <app-toast-container />
      <app-confirm-dialog />
    </div>
  `,
})
export class MarketplaceLayoutComponent {
  auth = inject(AuthService);
  cart = inject(CartService);
  /** Unidades en todos los carritos (uno por tienda), para la insignia del icono. */
  cartCount = computed(() => this.cart.storeCarts().reduce((sum, c) => sum + c.totalItems, 0));
  cartMenuOpen = signal(false);
  search = inject(MarketplaceSearchService);
  private router = inject(Router);

  /** El buscador está en todas las páginas; fuera de la portada, al escribir lleva a ella para mostrar los resultados. */
  onSearch(value: string): void {
    this.search.query.set(value);
    if (value.trim() && this.router.url.split(/[?#]/)[0] !== '/') void this.router.navigateByUrl('/');
  }
  menuOpen = signal(false);
  year = new Date().getFullYear();
  readonly adminUrl = environment.businessAdminUrl || environment.adminUrl || 'https://admin.faisen.tech';

  /** Un clic fuera de los menús (cuenta, carritos) los cierra (dentro, lo manejan sus propios botones). */
  closeMenuOnOutsideClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.menuOpen() && !target.closest('[data-user-menu]')) this.menuOpen.set(false);
    if (this.cartMenuOpen() && !target.closest('[data-cart-menu]')) this.cartMenuOpen.set(false);
  }

  logout(): void {
    this.auth.logout();
    this.menuOpen.set(false);
  }
}
